import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Fab,
  Container
} from '@mui/material';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Restaurant as RestaurantIcon
} from '@mui/icons-material';
import { chatApi, handleApiError } from '../services/api';
import type { ChatMessage, ChatSession, Restaurant } from '../types';

interface ChatBotProps {
  restaurant: Restaurant;
  isOpen: boolean;
  onToggle: () => void;
  fullScreen?: boolean;
}

const ChatBot: React.FC<ChatBotProps> = ({ restaurant, isOpen, onToggle, fullScreen = false }) => {
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [customerName, setCustomerName] = useState('');
  const [sessionStarted, setSessionStarted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startChatSession = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await chatApi.startChat(
        restaurant.restaurant_id,
        customerName || 'Guest'
      );

      setSession(response.session);
      setSessionStarted(true);
      
      // Add welcome message to local state
      const welcomeMessage: ChatMessage = {
        message_id: `welcome_${Date.now()}`,
        role: 'assistant',
        content: response.welcome_message,
        timestamp: new Date().toISOString(),
        type: 'welcome'
      };
      
      setMessages([welcomeMessage]);
    } catch (err) {
      console.error('Failed to start chat session:', err);
      setError(`Failed to start chat: ${handleApiError(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!session || !currentMessage.trim()) return;

    const userMessage: ChatMessage = {
      message_id: `user_${Date.now()}`,
      role: 'user',
      content: currentMessage.trim(),
      timestamp: new Date().toISOString()
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = currentMessage.trim();
    setCurrentMessage('');
    setLoading(true);

    try {
      const response = await chatApi.sendMessage(session.session_id, messageToSend);
      
      // Add AI response
      const aiMessage: ChatMessage = {
        message_id: `ai_${Date.now()}`,
        role: 'assistant',
        content: response.response,
        timestamp: response.timestamp,
        type: 'response'
      };
      
      setMessages(prev => [...prev, aiMessage]);
      setError('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(`Failed to send message: ${handleApiError(err)}`);
      
      // Add error message
      const errorMessage: ChatMessage = {
        message_id: `error_${Date.now()}`,
        role: 'assistant',
        content: "I'm sorry, I'm having trouble responding right now. Please try again.",
        timestamp: new Date().toISOString(),
        type: 'system'
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (sessionStarted) {
        sendMessage();
      } else {
        startChatSession();
      }
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const resetChat = () => {
    setSession(null);
    setMessages([]);
    setSessionStarted(false);
    setError('');
    setCustomerName('');
  };

  const chatContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'var(--color-background-chat)' }}>
      {/* Header */}
      {!fullScreen && (
        <AppBar position="static" elevation={1} sx={{ bgcolor: 'var(--color-background-paper)' }}>
          <Toolbar>
            <RestaurantIcon sx={{ mr: 1, color: 'var(--color-primary-500)' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" noWrap sx={{ color: 'var(--color-text-primary)' }}>
                {restaurant.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                Menu Assistant
              </Typography>
            </Box>
            <IconButton onClick={onToggle} sx={{ color: 'var(--color-text-secondary)' }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Chat Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {!sessionStarted ? (
          /* Start Chat Form */
          <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <BotIcon sx={{ fontSize: 64, color: 'var(--color-primary-500)', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ color: 'var(--color-text-primary)' }}>
                Welcome to {restaurant.name}!
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'var(--color-text-secondary)' }}>
                I'm your AI menu assistant. I can help you explore our {restaurant.cuisine_type} menu, 
                answer questions about ingredients, allergens, and make personalized recommendations.
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Your name (optional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ mb: 3 }}
              placeholder="Guest"
            />

            <Button
              variant="contained"
              size="large"
              onClick={startChatSession}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <ChatIcon />}
              fullWidth
              sx={{ 
                bgcolor: 'var(--color-primary-500)',
                '&:hover': { bgcolor: 'var(--color-primary-600)' }
              }}
            >
              {loading ? 'Starting Chat...' : 'Start Chatting'}
            </Button>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Container>
        ) : (
          /* Chat Messages */
          <>
            <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2, flexGrow: 1, overflow: 'auto' }}>
              <List disablePadding>
                {messages.map((message) => (
                  <ListItem
                    key={message.message_id}
                    sx={{
                      flexDirection: 'column',
                      alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                      pb: 2
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                        maxWidth: '80%'
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          mx: 1,
                          bgcolor: message.role === 'user' 
                            ? 'var(--color-primary-500)' 
                            : 'var(--color-secondary-500)'
                        }}
                      >
                        {message.role === 'user' ? <PersonIcon /> : <BotIcon />}
                      </Avatar>
                      
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          bgcolor: message.role === 'user' 
                            ? 'var(--color-chat-user-background)' 
                            : message.type === 'welcome' 
                              ? 'var(--color-chat-welcome-background)'
                              : 'var(--color-chat-assistant-background)',
                          color: message.role === 'user' 
                            ? 'var(--color-chat-user-text)' 
                            : message.type === 'welcome'
                              ? 'var(--color-chat-welcome-text)'
                              : 'var(--color-chat-assistant-text)',
                          borderRadius: 2,
                          border: `1px solid ${
                            message.role === 'user' 
                              ? 'var(--color-chat-user-border)' 
                              : message.type === 'welcome'
                                ? 'var(--color-chat-welcome-border)'
                                : 'var(--color-chat-assistant-border)'
                          }`
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {message.content}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.7,
                            display: 'block',
                            textAlign: message.role === 'user' ? 'right' : 'left'
                          }}
                        >
                          {formatTime(message.timestamp)}
                        </Typography>
                        {message.type === 'welcome' && (
                          <Chip 
                            label="Welcome" 
                            size="small" 
                            sx={{ 
                              mt: 1,
                              bgcolor: 'var(--color-secondary-100)',
                              color: 'var(--color-secondary-700)'
                            }} 
                          />
                        )}
                      </Paper>
                    </Box>
                  </ListItem>
                ))}
              </List>
              <div ref={messagesEndRef} />
            </Container>

            <Divider />

            {/* Message Input */}
            <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2, bgcolor: 'var(--color-background-paper)' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about our menu, ingredients, recommendations..."
                  disabled={loading}
                  variant="outlined"
                  size="small"
                />
                <IconButton
                  onClick={sendMessage}
                  disabled={loading || !currentMessage.trim()}
                  sx={{ 
                    mb: 0.5,
                    color: 'var(--color-primary-500)',
                    '&:hover': { bgcolor: 'var(--color-primary-50)' }
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <SendIcon />}
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                  {session && `Session: ${session.session_id.substring(0, 8)}...`}
                </Typography>
                <Button 
                  size="small" 
                  onClick={resetChat} 
                  sx={{ 
                    color: 'var(--color-secondary-600)',
                    '&:hover': { bgcolor: 'var(--color-secondary-50)' }
                  }}
                >
                  New Chat
                </Button>
              </Box>
            </Container>
          </>
        )}
      </Box>
    </Box>
  );

  // If fullScreen mode, return just the content without drawer wrapper
  if (fullScreen) {
    return chatContent;
  }

  return (
    <>
      {/* Chat Toggle Fab */}
      {!isOpen && (
        <Fab
          aria-label="chat"
          sx={{ 
            position: 'fixed', 
            bottom: 80, 
            right: 16,
            zIndex: 1000,
            bgcolor: 'var(--color-primary-500)',
            '&:hover': { bgcolor: 'var(--color-primary-600)' }
          }}
          onClick={onToggle}
        >
          <ChatIcon />
        </Fab>
      )}

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onToggle}
        variant="temporary"
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {chatContent}
      </Drawer>
    </>
  );
};

export default ChatBot; 